/*
Unified Notifications Hook
Combines unread messages, fresh deals, and new messages since login
Author: AI Assistant
Date: 2025-01-27
*/
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';

export interface UnifiedNotificationCounts {
  newMessagesSinceLogin: number;
  newDealsSinceLogin: number;
  total: number;
}

interface ExtendedUnreadCounts {
  newMessagesSinceLogin: number;
  newDealsSinceLogin: number;
  timestamp?: string;
}

interface UseUnifiedNotificationsReturn {
  counts: UnifiedNotificationCounts;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  reconnect: () => void;
  clearNotifications: () => void;
}

export const useUnifiedNotifications = (): UseUnifiedNotificationsReturn => {
  const { userData, isAuthenticated } = useAuth();
  const [counts, setCounts] = useState<UnifiedNotificationCounts>({
    newMessagesSinceLogin: 0,
    newDealsSinceLogin: 0,
    total: 0
  });
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const cleanup = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  };

  const connect = () => {
    const organizationId = userData?.organization?._id;
    
    if (!organizationId || !isAuthenticated) {
      setIsLoading(false);
      return;
    }

    // Clean up any existing connection
    cleanup();

    try {
      setError(null);
      setIsLoading(true);

      // Get auth token from localStorage for SSE authentication
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        setError('No authentication token available');
        setIsLoading(false);
        return;
      }

      // Get login timestamp for calculating new messages since login
      const loginTimestamp = localStorage.getItem('loginTimestamp') || '';
      
      const eventSource = new EventSource(
        `${import.meta.env.VITE_API_URL}messages/sse/unified-notifications/${organizationId}?token=${encodeURIComponent(authToken)}&loginTimestamp=${encodeURIComponent(loginTimestamp)}`,
        { withCredentials: true }
      );

      eventSource.onopen = () => {
        console.log('🔔 Unified notifications SSE connection opened');
        setIsConnected(true);
        setIsLoading(false);
        setError(null);
      };

      eventSource.onmessage = (event) => {
        try {
          const data: ExtendedUnreadCounts = JSON.parse(event.data);
          console.log('🔔 Unified notifications data received:', data);

          const newMessagesSinceLogin = data.newMessagesSinceLogin || 0;
          const newDealsSinceLogin = data.newDealsSinceLogin || 0;
          const total = newMessagesSinceLogin + newDealsSinceLogin;

          setCounts({
            newMessagesSinceLogin,
            newDealsSinceLogin,
            total
          });
          
          setError(null);
        } catch (parseError) {
          console.error('Error parsing unified notifications response:', parseError);
          setError('Error parsing server response');
        }
      };

      eventSource.onerror = (errorEvent) => {
        console.error('Unified notifications SSE connection error:', errorEvent);
        setIsConnected(false);
        setIsLoading(false);
        setError('Connection error');

        // Auto-reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('Attempting to reconnect unified notifications SSE...');
          connect();
        }, 5000);
      };

      // Handle custom error events from server
      eventSource.addEventListener('error', (event: any) => {
        try {
          const errorData = JSON.parse(event.data);
          setError(errorData.error || 'Server error');
        } catch {
          setError('Server error');
        }
      });

      eventSourceRef.current = eventSource;
    } catch (connectionError) {
      console.error('Failed to create unified notifications SSE connection:', connectionError);
      setError('Failed to connect');
      setIsLoading(false);
      setIsConnected(false);
    }
  };

  const reconnect = () => {
    connect();
  };

  const clearNotifications = useCallback(() => {
    console.log('🔔 Clearing notifications - updating loginTimestamp');
    
    // Update loginTimestamp to current time (this makes all current notifications "old")
    const newTimestamp = Date.now().toString();
    localStorage.setItem('loginTimestamp', newTimestamp);
    
    // Trigger reconnection to get updated counts with new timestamp
    cleanup();
    connect();
  }, [cleanup, connect]);

  useEffect(() => {
    // Always cleanup existing connection when organization changes
    cleanup();
    
    if (userData?.organization?._id && isAuthenticated) {
      // Small delay to ensure cleanup is complete
      setTimeout(() => {
        connect();
      }, 100);
    }

    // Cleanup on unmount or when dependencies change
    return cleanup;
  }, [userData?.organization?._id, isAuthenticated]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, []);

  return {
    counts,
    isConnected,
    isLoading,
    error,
    reconnect,
    clearNotifications
  };
};
